import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    Users,
    TrendingUp,
    AlertTriangle,
    CheckCircle2,
    Loader2,
    Target,
    Clock,
    BarChart3,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

export default function TeamAnalyticsPage() {
    const { t } = useTranslation();
    const { workspace } = useAuthStore();

    const { data, isLoading } = useQuery({
        queryKey: ['team-analytics', workspace?.id],
        queryFn: () => api.getTeamAnalytics(workspace!.id),
        enabled: !!workspace?.id,
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const analytics = data?.data;
    const teamProgress = analytics?.team_progress;
    const memberContributions = analytics?.member_contributions || [];
    const workloadDistribution = analytics?.workload_distribution || [];
    const atRiskItems = analytics?.at_risk_items || [];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <BarChart3 className="w-7 h-7 text-primary-600" />
                    {t('teamAnalytics.title', 'Team Analytics')}
                </h1>
                <p className="text-neutral-600 dark:text-neutral-400">
                    {t('teamAnalytics.description', 'Theo dõi hiệu suất và tiến độ của team')}
                </p>
            </div>

            {/* Team Progress Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard
                    label="Tiến độ OKR"
                    value={`${teamProgress?.average_progress || 0}%`}
                    icon={TrendingUp}
                    color="primary"
                />
                <StatCard
                    label="Mục tiêu"
                    value={teamProgress?.total_objectives?.toString() || '0'}
                    icon={Target}
                    color="neutral"
                />
                <StatCard
                    label="Đúng tiến độ"
                    value={teamProgress?.on_track?.toString() || '0'}
                    icon={CheckCircle2}
                    color="success"
                />
                <StatCard
                    label="Có nguy cơ"
                    value={teamProgress?.at_risk?.toString() || '0'}
                    icon={AlertTriangle}
                    color="warning"
                />
                <StatCard
                    label="Chậm tiến độ"
                    value={teamProgress?.off_track?.toString() || '0'}
                    icon={AlertTriangle}
                    color="danger"
                />
                <StatCard
                    label="Hoàn thành"
                    value={teamProgress?.completed?.toString() || '0'}
                    icon={CheckCircle2}
                    color="success"
                />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Member Contributions */}
                <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary-600" />
                        {t('teamAnalytics.contributions', 'Đóng góp thành viên')}
                    </h2>
                    {memberContributions.length === 0 ? (
                        <p className="text-sm text-neutral-500 text-center py-4">
                            {t('teamAnalytics.noContributions', 'Chưa có dữ liệu đóng góp')}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {memberContributions.map((member: any) => (
                                <div key={member.user_id} className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        {member.avatar_url ? (
                                            <img src={member.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                                        ) : (
                                            <span className="text-primary-600 font-medium text-sm">
                                                {member.full_name?.charAt(0)?.toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{member.full_name}</p>
                                        <div className="flex gap-4 text-xs text-neutral-500">
                                            <span>{member.tasks_completed_7d} tasks/7d</span>
                                            <span>{member.tasks_completed_30d} tasks/30d</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-lg font-bold text-primary-600">
                                            {member.tasks_completed_30d}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Workload Distribution */}
                <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-secondary-600" />
                        {t('teamAnalytics.workload', 'Phân bổ công việc')}
                    </h2>
                    {workloadDistribution.length === 0 ? (
                        <p className="text-sm text-neutral-500 text-center py-4">
                            {t('teamAnalytics.noWorkload', 'Chưa có dữ liệu phân bổ')}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {workloadDistribution.map((member: any) => (
                                <div key={member.user_id} className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-secondary-100 dark:bg-secondary-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                                        {member.avatar_url ? (
                                            <img src={member.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                                        ) : (
                                            <span className="text-secondary-600 font-medium text-sm">
                                                {member.full_name?.charAt(0)?.toUpperCase()}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{member.full_name}</p>
                                        <div className="flex gap-2">
                                            <span className="text-xs px-2 py-0.5 bg-neutral-100 dark:bg-neutral-700 rounded">
                                                {member.open_tasks} pending
                                            </span>
                                            {member.overdue_tasks > 0 && (
                                                <span className="text-xs px-2 py-0.5 bg-danger-50 text-danger-600 rounded">
                                                    {member.overdue_tasks} overdue
                                                </span>
                                            )}
                                            {member.high_priority_tasks > 0 && (
                                                <span className="text-xs px-2 py-0.5 bg-warning-50 text-warning-600 rounded">
                                                    {member.high_priority_tasks} high
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* At-Risk Items */}
            {atRiskItems.length > 0 && (
                <div className="card p-6 border-warning-200 dark:border-warning-600/30">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-warning-600">
                        <AlertTriangle className="w-5 h-5" />
                        {t('teamAnalytics.atRisk', 'Cần chú ý')} ({atRiskItems.length})
                    </h2>
                    <div className="space-y-2">
                        {atRiskItems.slice(0, 5).map((item: any) => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-warning-50 dark:bg-warning-600/10 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <span className={clsx(
                                        'text-xs px-2 py-0.5 rounded font-medium',
                                        item.type === 'objective' ? 'bg-primary-100 text-primary-600' : 'bg-neutral-100 text-neutral-600'
                                    )}>
                                        {item.type === 'objective' ? 'OKR' : 'Task'}
                                    </span>
                                    <span className="text-sm font-medium">{item.title}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    {item.type === 'objective' && (
                                        <span className="text-sm text-neutral-500">
                                            {Math.round(item.progress * 100)}%
                                        </span>
                                    )}
                                    {item.due_date && (
                                        <span className="text-xs text-danger-600">
                                            {new Date(item.due_date).toLocaleDateString('vi-VN')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({
    label,
    value,
    icon: Icon,
    color,
}: {
    label: string;
    value: string;
    icon: any;
    color: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
}) {
    const colorClasses = {
        primary: 'bg-primary-50 text-primary-600 dark:bg-primary-600/20',
        success: 'bg-success-50 text-success-600 dark:bg-success-600/20',
        warning: 'bg-warning-50 text-warning-600 dark:bg-warning-600/20',
        danger: 'bg-danger-50 text-danger-600 dark:bg-danger-600/20',
        neutral: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700',
    };

    return (
        <div className="card p-4 text-center">
            <div className={clsx('w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center', colorClasses[color])}>
                <Icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-neutral-500">{label}</p>
        </div>
    );
}
